import { useCMSPageField } from '@/cms/fields'

import { useIntl } from '@/intl'
import { getBrandName } from '@/scopes/common/infrastructure/analytics/brandNameMapper'
import { useConfiguration } from '@/scopes/common/infrastructure/config/ConfigProvider'
import { useCommonDependency } from '@/scopes/common/infrastructure/dependencies/common'
import { useAccommodationContext } from '@/scopes/prices/components/AccommodationContext'
import Layout from '@/scopes/prices/components/Layout'
import { PricesUseCasesContext } from '@/scopes/prices/components/PricesUseCasesContext'
import { alternativeFilterSelector } from '@/scopes/prices/components/selectors'
import {
  FiltersInPricesValues,
  PricesFilterType,
} from '@/scopes/prices/domain/filters/types'
import { usePricesSelector } from '@/scopes/prices/infrastructure/usePricesSelector'
import { Chip, Icon, SpaceBetween, tk, Typography } from '@sunwebgroup/ui-components'
import * as React from 'react'
import { styled } from 'styled-components'

const WarningIcon = styled(Icon)`
  color: ${tk('color_semantic_bg_warning')};
`

const Wrapper = styled(SpaceBetween)`
  display: flex;
  flex-direction: column;
`

const BoldChip = styled(Chip)`
  cursor: pointer;
  ${Typography} {
    font-weight: 600;
  }
`

export function AlternativeFilters() {
  const intl = useIntl()
  const { initPrices } = React.useContext(PricesUseCasesContext)
  const brandName = useConfiguration('siteName')
  const accoBrand = getBrandName(brandName)
  const { contextItemId, accoId, bookingGateId, promotedPriceId } = useAccommodationContext()
  const queryStringClient = useCommonDependency('queryStringClient')
  const analytics = useCMSPageField('PDP.pricetable.analytics')

  const reset = async () => {
    const originList = queryStringClient.getValue('originList')
    await initPrices({
      accoId,
      bookingGateId,
      contextItemId,
      analytics: analytics ? { ...analytics, originList: originList || undefined, accoBrand } : null,
      initialFilterSelection: {
        Participants: [
          {
            adults: 2,
            children: [],
            babies: [],
          },
        ],
      },
      promotedPriceId,
    })
  }

  return (
    <Layout.Root>
      <WarningIcon glyph="IconWarningTriangle" size="lg" />
      <Layout.Content>
        <SpaceBetween direction="y" spacing={tk('spacing_md')}>
          <Layout.TitleDescription>
            <Typography $variant="body-lg-bold">
              {intl.formatMessage({ id: 'prices_alternative_filters_title' })}
            </Typography>
            <Typography $variant="body-md">
              {intl.formatMessage({ id: 'prices_alternative_filters_description' })}
            </Typography>
          </Layout.TitleDescription>
          <AlternativeFilter filterType="Month" />
          <AlternativeFilter filterType="Duration" />
          <AlternativeFilter filterType="DepartureAirport" />
          <AlternativeFilter filterType="RoomType" />
          <AlternativeFilter filterType="Mealplan" />
          <AlternativeFilter filterType="TransportType" />
          <Wrapper direction="y" spacing={tk('spacing_2xs')}>
            <Typography>{intl.formatMessage({ id: 'prices_alternative_filters_reset_filters_title' })}</Typography>
            <div>
              <BoldChip onClick={() => reset()}>
                {intl.formatMessage({ id: 'prices_alternative_filters_reset_filters_description' })}
              </BoldChip>
            </div>
          </Wrapper>
        </SpaceBetween>
      </Layout.Content>
    </Layout.Root>
  )
}

const ChipsWrapper = styled(SpaceBetween)`
  display: flex;
  flex-wrap: wrap;
`

function AlternativeFilter({ filterType }: { filterType: PricesFilterType }) {
  const data = usePricesSelector(alternativeFilterSelector, filterType)
  const { selectFilter } = React.useContext(PricesUseCasesContext)
  const setSelection = (selection: FiltersInPricesValues) => selectFilter({ type: filterType, selection })

  if (!data)
    return null

  return (
    <Wrapper direction="y" spacing={tk('spacing_2xs')}>
      <Typography>{data.title}</Typography>
      <ChipsWrapper direction="x" spacing={tk('spacing_sm')}>
        {data.options.map(option => (
          <BoldChip key={option.value} value={option.value} onClick={() => setSelection([option.value])}>
            {option.text}
          </BoldChip>
        ))}
      </ChipsWrapper>
    </Wrapper>
  )
}
